import * as React from 'react';

interface State {
  err: Error | null;
}

/**
 * Catches render-time exceptions anywhere below it and shows a debug
 * card with the message + stack instead of letting React unmount the
 * tree (which would render the page entirely blank against the dark
 * theme background — exactly the "blue screen" symptom we're guarding
 * against).
 */
export class ErrorBoundary extends React.Component<{ children: React.ReactNode }, State> {
  override state: State = { err: null };

  static getDerivedStateFromError(err: Error): State {
    return { err };
  }

  override componentDidCatch(err: Error, info: React.ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught:', err, info.componentStack);
  }

  override render(): React.ReactNode {
    if (!this.state.err) return this.props.children;
    const { err } = this.state;
    return (
      <div
        style={{
          fontFamily:
            "-apple-system, BlinkMacSystemFont, 'Segoe UI', Inter, Roboto, sans-serif",
          minHeight: '100vh',
          background: '#0b1220',
          color: '#e5edff',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          padding: '4rem 1rem',
        }}
      >
        <div
          style={{
            maxWidth: 720,
            width: '100%',
            border: '1px solid #ef4444',
            background: '#1f0d12',
            borderRadius: 12,
            padding: '1.5rem 1.75rem',
          }}
        >
          <div style={{ fontSize: 12, color: '#fca5a5', textTransform: 'uppercase', letterSpacing: 2 }}>
            Render-Fehler
          </div>
          <h1 style={{ margin: '0.5rem 0 1rem', fontSize: '1.05rem' }}>
            {err.name}: {err.message}
          </h1>
          <pre
            style={{
              fontSize: 11,
              lineHeight: 1.5,
              background: '#0b1220',
              border: '1px solid #1f2a44',
              borderRadius: 6,
              padding: '0.75rem',
              overflow: 'auto',
              maxHeight: 360,
              color: '#8392b8',
            }}
          >
            {err.stack ?? '(kein Stack)'}
          </pre>
          <div style={{ marginTop: '1rem', display: 'flex', gap: 8 }}>
            <button
              type="button"
              onClick={() => {
                try {
                  localStorage.clear();
                  sessionStorage.clear();
                  // eslint-disable-next-line no-console
                  console.log('Storage cleared');
                } catch {
                  /* ignore */
                }
                window.location.reload();
              }}
              style={{
                background: '#5eead4',
                color: '#0b1220',
                border: 0,
                borderRadius: 6,
                padding: '0.5rem 0.875rem',
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              Storage leeren + Neu laden
            </button>
            <button
              type="button"
              onClick={() => window.location.reload()}
              style={{
                background: 'transparent',
                color: '#e5edff',
                border: '1px solid #1f2a44',
                borderRadius: 6,
                padding: '0.5rem 0.875rem',
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              Neu laden
            </button>
          </div>
        </div>
      </div>
    );
  }
}
